import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdArrowBack,
  MdChevronRight,
  MdOutlineContentCopy,
  MdRefresh,
  MdCheck,
} from "react-icons/md";

import HyperLink from "../../../../../../Components/HyperLink/HyperLink";
import CircularLoader from "../../../../../../Components/CircularLoader/CircularLoader";
import { fetchShipmentDetailsHistoryById } from "../../../../../../services/ShipperServices/Shipments/shipments.services";
import { linkColor } from "../../../../../Shipper/Customer/MainPages/ShipPage/Components/LinkColor/linkColors";
import { TONES, parcelTone } from "./tones";

// A parcel's number chip, in its product's link colour as on Parcels.
const ParcelChip = ({ parcel, number }) => {
  const color = parcel?.link_key ? linkColor(parcel.link_key) : null;
  return (
    <span
      className={`w-8 shrink-0 rounded px-1 py-0.5 text-center text-[10.5px] font-semibold ${
        color ? "" : "bg-ui-surface-sunken text-ui-text-muted"
      }`}
      style={
        color
          ? {
              color,
              backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`,
            }
          : undefined
      }
    >
      P{number}
    </span>
  );
};

// The shipment as a whole (its own tracking number's updates), next to the
// parcels in the list.
export const WHOLE_SHIPMENT = "shipment";

const lengthUnit = (unit) => (unit === "Metric" ? "cm" : "in");
const weightUnit = (unit) => (unit === "Metric" ? "kg" : "lb");

// "2026-10-19 13:00:00" → "2026-10-19 · 13:00".
const when = (date) =>
  date
    ? `${String(date).slice(0, 10)}${
        String(date).length > 11 ? ` · ${String(date).slice(11, 16)}` : ""
      }`
    : "";

/**
 * Tracking updates oldest first, so the newest reads as where it is now.
 * By date when every update has one that reads; as they came otherwise.
 */
export const byDate = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const timed = list.map((row, index) => ({
    row,
    index,
    time: Date.parse(String(row?.StatusDate ?? "").replace(" ", "T")),
  }));
  if (timed.some(({ time }) => Number.isNaN(time))) return list;
  return timed
    .sort((a, b) => a.time - b.time || a.index - b.index)
    .map(({ row }) => row);
};

const Chip = ({ status, className = "" }) =>
  status ? (
    <span
      className={`shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium lg:text-[10.5px] ${TONES[parcelTone(status)]} ${className}`}
    >
      {status}
    </span>
  ) : null;

const Timeline = ({ rows }) => (
  <ol className="m-0 list-none p-0">
    {rows.map((row, index) => {
      const isLatest = index === rows.length - 1;
      return (
        <li
          key={row?.id ?? index}
          className="relative flex gap-2.5 pb-3 last:pb-0"
        >
          <div className="flex flex-col items-center self-stretch">
            <span
              className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                isLatest ? "bg-state-success-dot" : "bg-ui-border-strong"
              }`}
            />
            {index < rows.length - 1 && (
              <span className="mt-0.5 w-px flex-1 bg-ui-border" />
            )}
          </div>
          <div className="min-w-0 flex-1 pb-0.5">
            <div className="flex items-baseline justify-between gap-2">
              <span
                className={`min-w-0 break-words text-[12.5px] text-ui-text lg:text-[12px] ${
                  isLatest ? "font-semibold" : "font-medium"
                }`}
              >
                {row?.ShipmentStatus ?? "-"}
              </span>
              <span className="shrink-0 text-[11px] text-ui-text-subtle lg:text-[10.5px]">
                {when(row?.StatusDate)}
              </span>
            </div>
            {row?.LastLocation && (
              <div className="break-words text-[12px] text-ui-text-muted lg:text-[11px]">
                {row.LastLocation}
              </div>
            )}
          </div>
        </li>
      );
    })}
  </ol>
);

const Fact = ({ label, children }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-ui-text-subtle">
      {label}
    </div>
    <div className="break-words text-[12.5px] text-ui-text lg:text-[12px]">
      {children}
    </div>
  </div>
);

const Note = ({ children }) => (
  <p className="m-0 py-6 text-center text-[12px] text-ui-text-subtle">
    {children}
  </p>
);

/**
 * Tracking, parcels first: every parcel with its carrier status (and the
 * shipment's own updates as one more line); a parcel opens to its detail —
 * tracking numbers, size and weight, where it is now and each update with
 * its date, time and place. `selected` (a parcel id, WHOLE_SHIPMENT or
 * null) and `onSelect` are the view's, so a parcel row elsewhere can open
 * one here.
 */
export default function OrderTracking({ data, selected, onSelect, panelRef }) {
  const { t } = useTranslation("warehouse");
  const shipment = data?.shipment;
  const unit = shipment?.unit;
  const parcels = shipment?.shipment_details || [];
  const carrierParcels =
    shipment?.c_p_shipment_details || data?.c_p_shipment_details || [];
  const shipmentUpdates = byDate(shipment?.shipment_histories);

  // Each parcel's updates, loaded when it's first opened:
  // { [id]: { loading, rows, error } }.
  const [histories, setHistories] = useState({});
  const [copied, setCopied] = useState(false);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const parcelIndex = parcels.findIndex(
    (parcel) => parcel?.id != null && String(parcel.id) === String(selected),
  );
  const parcel = parcelIndex === -1 ? null : parcels[parcelIndex];
  const showingShipment =
    selected === WHOLE_SHIPMENT && shipmentUpdates.length > 0;

  const load = useCallback(async (id) => {
    setHistories((current) => ({
      ...current,
      [id]: { loading: true, rows: current[id]?.rows ?? null, error: false },
    }));
    const response = await fetchShipmentDetailsHistoryById(id);
    if (!live.current) return;
    setHistories((current) => ({
      ...current,
      [id]: response?.error
        ? { loading: false, rows: current[id]?.rows ?? null, error: true }
        : {
            loading: false,
            rows: byDate(response?.data?.result),
            error: false,
          },
    }));
  }, []);

  const parcelId = parcel?.id;
  useEffect(() => {
    if (parcelId != null && !histories[parcelId]) load(parcelId);
    // Loaded once per parcel; Refresh loads it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelId]);

  const copy = (text) => {
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  const back = (
    <button
      type="button"
      onClick={() => onSelect(null)}
      aria-label="All parcels"
      title="All parcels"
      className="-my-1.5 -ml-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-ui-text-muted transition-colors hover:bg-ui-row-hover hover:text-ui-text lg:h-7 lg:w-7 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
    >
      <MdArrowBack size={17} />
    </button>
  );

  let header;
  let body;

  if (parcel) {
    const history = histories[parcel.id];
    const rows = history?.rows ?? [];
    const latest = rows[rows.length - 1];
    const carrierId = carrierParcels[parcelIndex]?.shipment_tracking_id;
    const size = [
      parcel?.length || parcel?.width || parcel?.height
        ? `${parcel?.length ?? 0} × ${parcel?.width ?? 0} × ${parcel?.height ?? 0} ${lengthUnit(unit)}`
        : null,
      parcel?.weight ? `${parcel.weight} ${weightUnit(unit)}` : null,
      parseFloat(parcel?.volume) ? `${parcel.volume} cbm` : null,
    ]
      .filter(Boolean)
      .join(" · ");

    header = (
      <>
        {back}
        <ParcelChip parcel={parcel} number={parcelIndex + 1} />
        <h3 className="m-0 min-w-0 flex-1 truncate text-[13.5px] font-semibold text-ui-text">
          {parcel?.parcel_id || "Parcel"}
        </h3>
        <button
          type="button"
          onClick={() => load(parcel.id)}
          disabled={history?.loading}
          aria-label="Refresh this parcel's tracking"
          title="Refresh"
          className="-my-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-ui-text-subtle transition-colors hover:bg-ui-row-hover hover:text-ui-text disabled:opacity-50 lg:h-7 lg:w-7 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10"
        >
          <MdRefresh size={16} />
        </button>
      </>
    );

    body = (
      <div className="px-3 py-2.5">
        {parcel?.description && (
          <div className="mb-2 break-words text-[13px] font-medium text-ui-text">
            {parcel.description}
          </div>
        )}

        {/* Where it is now: the carrier's latest status on the parcel. */}
        <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md bg-ui-surface-sunken px-2.5 py-2">
          <span className="text-[11px] uppercase tracking-wide text-ui-text-subtle">
            Now
          </span>
          {parcel?.tracking_status || latest?.ShipmentStatus ? (
            <Chip
              status={parcel?.tracking_status || latest?.ShipmentStatus}
              className="!text-[12px]"
            />
          ) : (
            <span className="text-[12px] text-ui-text-muted">
              No status yet
            </span>
          )}
          {latest?.StatusDate && (
            <span className="ml-auto text-[11.5px] text-ui-text-subtle">
              {when(latest.StatusDate)}
            </span>
          )}
        </div>

        <div className="mb-3 grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
          <Fact label="Parcel / tracking ID">
            {parcel?.parcel_id ? (
              <span className="flex items-center gap-1">
                <span className="min-w-0 break-all">
                  <HyperLink code={parcel.parcel_id} />
                </span>
                <button
                  type="button"
                  onClick={() => copy(parcel.parcel_id)}
                  aria-label="Copy the tracking ID"
                  title={copied ? "Copied" : "Copy"}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-ui-text-subtle hover:text-brand lg:h-6 lg:w-6"
                >
                  {copied ? (
                    <MdCheck size={14} />
                  ) : (
                    <MdOutlineContentCopy size={13} />
                  )}
                </button>
              </span>
            ) : (
              "—"
            )}
          </Fact>
          {carrierId && (
            <Fact label="Carrier tracking">
              <span className="break-all">
                <HyperLink code={carrierId} />
              </span>
            </Fact>
          )}
          {size && <Fact label="Size · weight">{size}</Fact>}
        </div>

        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ui-text-subtle">
          Updates
        </div>
        {history?.loading && !rows.length ? (
          <div className="flex items-center justify-center gap-2 py-5 text-[12px] text-ui-text-subtle">
            <CircularLoader size={14} /> Loading tracking…
          </div>
        ) : history?.error && !rows.length ? (
          <div className="py-4 text-center text-[12px] text-ui-text-subtle">
            Couldn't load this parcel's tracking.{" "}
            <button
              type="button"
              onClick={() => load(parcel.id)}
              className="min-h-[40px] font-semibold text-brand-text underline lg:min-h-0"
            >
              Try again
            </button>
          </div>
        ) : rows.length ? (
          <Timeline rows={rows} />
        ) : (
          <Note>No tracking updates for this parcel yet</Note>
        )}
      </div>
    );
  } else if (showingShipment) {
    header = (
      <>
        {back}
        <h3 className="m-0 min-w-0 flex-1 truncate text-[13.5px] font-semibold text-ui-text">
          Whole shipment
        </h3>
        <Chip status={shipment?.shipment_status} />
      </>
    );
    body = (
      <div className="px-3 py-2.5">
        <Timeline rows={shipmentUpdates} />
      </div>
    );
  } else {
    header = (
      <>
        <h3 className="m-0 min-w-0 flex-1 text-[13.5px] font-semibold text-ui-text">
          {t("orderView.tracking")}
        </h3>
        {shipment?.shipment_status && (
          <span className="shrink-0 rounded-md bg-state-success-bg px-2 py-0.5 text-[11px] font-medium text-state-success-text">
            {shipment.shipment_status}
          </span>
        )}
      </>
    );

    if (!parcels.length) {
      // No parcels to list: the shipment's own updates, as before.
      body = shipmentUpdates.length ? (
        <div className="px-3 py-2.5">
          <Timeline rows={shipmentUpdates} />
        </div>
      ) : (
        <Note>
          {shipment?.shipment_histories
            ? t("orderView.noShipmentHistoryAvailable")
            : t("orderView.shipmentNotAvailable")}
        </Note>
      );
    } else {
      const latestShipment = shipmentUpdates[shipmentUpdates.length - 1];
      body = (
        <ul className="m-0 list-none p-0">
          {parcels.map((item, index) => (
            <li
              key={item?.id ?? index}
              className="border-b border-ui-border-subtle last:border-b-0"
            >
              <button
                type="button"
                onClick={() => item?.id != null && onSelect(item.id)}
                className="flex min-h-[48px] w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-ui-row-hover lg:min-h-0"
              >
                <ParcelChip parcel={item} number={index + 1} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-mono text-[12px] text-ui-text lg:text-[11.5px]">
                    {item?.parcel_id || "No tracking ID"}
                  </span>
                  {item?.description && (
                    <span className="block truncate text-[11.5px] text-ui-text-subtle lg:text-[11px]">
                      {item.description}
                    </span>
                  )}
                </span>
                <Chip status={item?.tracking_status} />
                <MdChevronRight
                  size={17}
                  aria-hidden="true"
                  className="shrink-0 text-ui-text-subtle"
                />
              </button>
            </li>
          ))}
          {shipmentUpdates.length > 0 && (
            <li>
              <button
                type="button"
                onClick={() => onSelect(WHOLE_SHIPMENT)}
                className="flex min-h-[48px] w-full items-center gap-2.5 border-t border-ui-border bg-ui-surface-sunken px-3 py-2 text-left transition-colors hover:bg-ui-row-hover lg:min-h-0"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-medium text-ui-text lg:text-[12px]">
                    Whole shipment
                  </span>
                  <span className="block truncate text-[11.5px] text-ui-text-subtle lg:text-[11px]">
                    {shipmentUpdates.length}{" "}
                    {shipmentUpdates.length === 1 ? "update" : "updates"}
                    {latestShipment?.StatusDate
                      ? ` · last ${when(latestShipment.StatusDate)}`
                      : ""}
                  </span>
                </span>
                <Chip status={latestShipment?.ShipmentStatus} />
                <MdChevronRight
                  size={17}
                  aria-hidden="true"
                  className="shrink-0 text-ui-text-subtle"
                />
              </button>
            </li>
          )}
        </ul>
      );
    }
  }

  return (
    <section
      ref={panelRef}
      aria-label="Tracking"
      // An open parcel gets most of the column (comments keep the rest).
      className={`flex max-h-[480px] shrink-0 scroll-mt-2 flex-col overflow-hidden rounded-lg border border-ui-border bg-ui-surface ${
        parcel || showingShipment ? "lg:max-h-[75%]" : "lg:max-h-[55%]"
      }`}
    >
      <header className="flex shrink-0 items-center gap-2 border-b border-ui-border px-3 py-2">
        {header}
      </header>
      <div className="thin__scrollbar min-h-0 flex-1 overflow-y-auto">
        {body}
      </div>
    </section>
  );
}
